import withStyles from '@material-ui/core/styles/withStyles';
import React from 'react';
import {
  Query,
  ReferenceArrayInput,
  required,
  showNotification,
  Loading,
  Error
} from 'react-admin';
import { FormSpy } from 'react-final-form';

import ResourceUploadApi from '../../../../core/apis/ResourceUploadApi';
import CheckBoxInput from '../../../../core/components/inputs/CheckBoxInput';
import DoubleListArrayInput from '../../../../core/components/inputs/DoubleListArrayInput';
import { connect } from 'react-redux';
import { getIcError } from '../../../../core/providers/utils';
import { WarningIcon } from '../../../../core/themes/icons/WarningIcon';

import { Grid, Typography } from '@material-ui/core/';
import dataProvider from '../../../../core/providers/IccUserDataProvider';
import CustomSimpleForm from '../../../../core/components/form/CustomSimpleForm';
import {
  additionalActionsStyles,
  FundDoubleListStyles,
  notificationStyle
} from './styles';

const validateFunds = value => {
  if (value && value.length < 1) {
    return 'At least one Fund must be selected';
  }
  return undefined;
};

// Returns true if:
// - tenant is NeubergerBerman
// - Toranomon Private Equity 1 AIV, L.P. fund (fund with fundId = fi_f_2FA1E30FA6EF436) is among the selected funds
const isJapanFundSelected = value => {
  const isNBGClient = sessionStorage.getItem('tenant') === 'NeubergerBerman';

  return value && value.includes('fi_f_2FA1E30FA6EF436') && isNBGClient;
};

const checkJapanFund = (value, handleJapanFund) => {
  handleJapanFund(isJapanFundSelected(value));
};

const FundStepAdditionalActions = withStyles(additionalActionsStyles)(
  ({ classes, readOnly, fundStepAdditionalActionsHandler }) => {
    return (
      <CheckBoxInput
        name="metadata.allInvestorsChecked"
        label="Submit to all Investors of selected Funds"
        onChange={fundStepAdditionalActionsHandler}
        className={classes.checkbox}
        disabled={readOnly}
      />
    );
  }
);

export const NotificationForJapanFund = withStyles(notificationStyle)(
  ({ classes }) => {
    return (
      <Grid item className={classes.notificationContainer}>
        <WarningIcon className={classes.warningIcon}></WarningIcon>
        <Typography variant="caption" align="center">
          Please make sure the document has been approved by the Japan team!
        </Typography>
      </Grid>
    );
  }
);

/**
 * Wrap DoubleListArrayInput with list of fund families ==> groups property
 */
const FundDoubleList = withStyles(FundDoubleListStyles)(
  ({
    classes,
    readOnly,
    bulkMode,
    tagsList,
    choices,
    input,
    label,
    meta,
    isFundLevelFlowAvailable,
    ...props
  }) => (
    <Query type="GET_LIST" resource="fundfamilies" payload={{}}>
      {({ data, loading, error }) => {
        if (!loading && !error) {
          return (
            <DoubleListArrayInput
              {...props}
              readOnly={readOnly}
              choices={choices}
              input={input}
              meta={meta}
              label={label}
              groups={data}
              filterByGroup={true}
              filterByGroupLabel="All Fund Families"
              groupId="id"
              groupLabel="name"
              groupBy="fundFamilyId"
              classes={{
                containerClass:
                  !bulkMode && !isFundLevelFlowAvailable
                    ? classes.containerClass
                    : {}
              }}
              additionalActions={
                !bulkMode && !isFundLevelFlowAvailable ? (
                  <FundStepAdditionalActions
                    fundStepAdditionalActionsHandler={
                      props.fundStepAdditionalActionsHandler
                    }
                    readOnly={readOnly}
                  />
                ) : null
              }
              // It is requested by Neuberger (ADO-825793, ADO-899178) that when uploading a document, Fund Admins at Neuberger
              //needs to be displayed a warning message if they select Toranomon Private Equity 1 AIV, L.P. fund
              showNotification={
                props.isJapanFundSelected ? <NotificationForJapanFund /> : null
              }
              checkJapanFund={checkJapanFund}
              handleJapanFund={props.handleJapanFund}
              allowTagFilter={true}
              tagsList={tagsList}
              source={'fundtags'}
            />
          );
        } else if (loading) {
          return <Loading className={classes.loading} />;
        } else if (error) {
          return <Error />;
        }
      }}
    </Query>
  )
);

class FundLevelTree extends React.Component {
  formApi;
  pristine;

  constructor(props) {
    super(props);

    // Set initial state of isJapanFundSelected calculated based on initial funds selected
    this.state = {
      tagsList: [],
      isJapanFundSelected: isJapanFundSelected(
        props.resourceupload.fundIds && props.resourceupload.fundIds[0] !== '*'
          ? props.resourceupload.fundIds
          : []
      )
    };
  }

  componentDidMount = () => {
    dataProvider('GET_LIST', 'fundtags', {
      paginated: false,
      filter: {
        orderBy: 'name',
        orderDirection: 'ASC'
      }
    })
      .then(({ data }) => {
        if (data) {
          this.setState({
            tagsList: data
          });
        }
      })
      .catch(error => console.error('Error fetching fund tags :', error));
  };

  // Set state for isJapanFundSelected to provided value
  handleJapanFund = value => {
    this.setState({
      isJapanFundSelected: value
    });
  };

  render() {
    const {
      onFormInit,
      formName,
      resource,
      resourceupload,
      bulkMode,
      isFundLevelFlowAvailable
    } = this.props;
    //for batches uploaded via API disabled Funds Step changes if batch is invalid
    const stepReadOnly = !!(
      resourceupload.uploadVia && resourceupload.uploadVia === 'API'
    );
    return (
      <CustomSimpleForm
        form={formName}
        save={this.setFunds}
        toolbar={null}
        resource={resource}
        initialValues={{
          metadata: {
            fundIds:
              resourceupload.fundIds && resourceupload.fundIds[0] !== '*' // To Do : if fundIds[0] = "*" check ALL Funds (not created yet) checkbox
                ? resourceupload.fundIds
                : [],
            allInvestorsChecked: resourceupload.allInvestors
          }
        }}
        variant="standard"
        margin="normal"
      >
        <FormSpy subscription={{ pristine: true }}>
          {formprops => {
            // Store current form API object
            if (this.formApi === undefined && onFormInit !== undefined) {
              onFormInit(formprops.form);
            }
            this.formApi = formprops.form;
            this.pristine = formprops.pristine;

            return (
              <ReferenceArrayInput
                label="Funds"
                resource={resource}
                reference="funds"
                source="metadata.fundIds"
                validate={[validateFunds, required()]}
                perPage={-1}
                sort={{ field: 'name', order: 'ASC' }}
                filter={{ returnFundTags: 'true' }}
              >
                <FundDoubleList
                  fundStepAdditionalActionsHandler={
                    this.props.handleFundLevelDocumentsChanged
                  }
                  bulkMode={bulkMode}
                  readOnly={stepReadOnly}
                  isJapanFundSelected={this.state.isJapanFundSelected}
                  handleJapanFund={this.handleJapanFund}
                  tagsList={this.state.tagsList}
                  isFundLevelFlowAvailable={isFundLevelFlowAvailable}
                />
              </ReferenceArrayInput>
            );
          }}
        </FormSpy>
      </CustomSimpleForm>
    );
  }

  /**
   * Update list of funds / fund families with /resourceupload API
   */
  setFunds = async values => {
    const {
      resourceupload,
      onSubmitEnd,
      showNotification,
      disableActionButtons,
      handleResetActionButtons,
      handleUploadChange,
      uploadChanged,
      fundLevelDocument,
      isFundLevelFlowAvailable
    } = this.props;
    disableActionButtons();

    // If the form is untouched then do nothing and go to next step;
    // special case : If form is untouched but file or document profile is changed on Details Step and it's fundLevel upload then
    // set again resourcefundInvestors
    if (this.pristine) {
      if (
        (values.metadata.allInvestorsChecked || fundLevelDocument) &&
        uploadChanged
      ) {
        await this.setAllInvestors(
          resourceupload,
          onSubmitEnd,
          showNotification,
          handleResetActionButtons,
          handleUploadChange
        );
      } else {
        onSubmitEnd(resourceupload);
      }
    } else {
      const metadata = {
        documentDate: resourceupload.documentDate,
        dueDate: resourceupload.dueDate,
        documentTitle: resourceupload.documentTitle,
        documentProfileId: resourceupload.documentProfileId,
        documentTypeId: resourceupload.documentTypeId,
        profileData: resourceupload.profileData,
        fileName: resourceupload.fileName,
        year: resourceupload.year,
        quarter: resourceupload.quarter,
        fundFamilyId: null,
        fundIds: values.metadata.fundIds,
        allInvestors: isFundLevelFlowAvailable
          ? resourceupload.allInvestors
          : values.metadata.allInvestorsChecked
      };

      const formData = new FormData();
      formData.append('resourceUploadId', resourceupload.resourceUploadId);
      formData.append('metadata', JSON.stringify(metadata));

      try {
        const updatedResourceupload = await ResourceUploadApi.updateResourceUpload(
          formData
        );
        if (values.metadata.allInvestorsChecked || fundLevelDocument) {
          await this.setAllInvestors(
            updatedResourceupload,
            onSubmitEnd,
            showNotification,
            handleResetActionButtons,
            handleUploadChange
          );
        } else if (onSubmitEnd) {
          onSubmitEnd(updatedResourceupload);
        }
      } catch (error) {
        handleResetActionButtons();
        showNotification('ra.notification.server', 'error', {
          messageArgs: { message: getIcError(error) }
        });
      }
    }
  };

  setAllInvestors = async (
    resourceUpload,
    onSubmitEnd,
    showNotification,
    handleResetActionButtons,
    handleUploadChange
  ) => {
    const resourceUploadId = resourceUpload.resourceUploadId;
    const investorId = '*';
    const body = resourceUpload.fundIds.map(fundId => {
      return { resourceUploadId, fundId, investorId };
    });
    try {
      await ResourceUploadApi.setFundinvestors(body);
      handleUploadChange(false);
      if (onSubmitEnd) {
        onSubmitEnd(resourceUpload);
      }
    } catch (error) {
      handleResetActionButtons();
      showNotification('ra.notification.server', 'error', {
        messageArgs: { message: getIcError(error) }
      });
    }
  };
}

const mapDispatchToProps = {
  showNotification
};

export default connect(null, mapDispatchToProps)(FundLevelTree);
